import React from "react";
import { formatDisplayDatetime } from "utils/time-utils";
import { Cell, Month, WeekDay } from "./TimelineComponents";
import { useTimezone } from "hooks/useTimezone";
import { DATE_FORMATTER_NO_TIME } from "utils/constants";

interface TimelineProps {
  range: [Date, Date];
  data: { date: Date; value: number }[];
  ignoreCurrentDay: boolean;
  colorFunc: (value: number) => string;
}

const Timeline = ({ range, data, ignoreCurrentDay, colorFunc }: TimelineProps) => {
  const tz = useTimezone();
  const days = Math.ceil((range[1].getTime() - range[0].getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const startDayOfWeek = range[0].getDay() - 1;
  const endDayOfWeek = range[1].getDay() - 1;
  const totalCells = days + startDayOfWeek + (6 - endDayOfWeek);
  const weeks = Math.ceil(totalCells / 7);
  const cells = Array.from(new Array(totalCells));
  const weekDays = Array.from(new Array(7).keys());
  const months = Array.from(new Array(weeks));

  const startDate = range[0];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="timeline">
      <div className="timeline-months">
        {months.map((_, index) => (
          <Month key={index} index={index} startDate={startDate} />
        ))}
      </div>

      <div className="timeline-body">
        <div className="timeline-weekdays">
          {weekDays.map(index => (
            <WeekDay key={index} index={index} />
          ))}
        </div>

        <div className="timeline-cells">
          {cells.map((_, index) => {
            const adjustedIndex = index - startDayOfWeek;
            if (adjustedIndex < 0 || adjustedIndex >= days) {
              return <Cell key={adjustedIndex} color="transparent" />;
            }
            const date = new Date(startDate);
            date.setDate(date.getDate() + adjustedIndex);

            const dateString = formatDisplayDatetime(date, tz.display, DATE_FORMATTER_NO_TIME);
            const dataPoint = data.find(
              d => formatDisplayDatetime(new Date(d.date), tz.display, DATE_FORMATTER_NO_TIME) === dateString
            );

            let color = "#ffffff";

            if (ignoreCurrentDay && date.getTime() === today.getTime()) {
              color = "";
            } else if (dataPoint) {
              color = colorFunc(dataPoint.value);
            }

            const value = dataPoint ? dataPoint.value : null;

            const isFirstMonthDay = adjustedIndex === 0;
            const lastDayInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
            const isMonday = date.getDay() === 1;
            const isSunday = date.getDay() === 0;

            let className = "";
            if (date.getDate() > new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate() - 7) className += " month-end";
            if (isMonday) className += " monday";
            if (isSunday) className += " sunday";
            if (isFirstMonthDay) className += " first-month-day";
            if (date.getDate() === lastDayInMonth) className += " last-month-day";
            if (adjustedIndex < 7) className += " first-week";

            return (
              <Cell key={adjustedIndex} color={color} className={className} value={value ?? undefined} date={dateString} />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Timeline;
